import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  type ClockFormat,
  dateParts,
  formatDateTime,
  formatDay,
  formatTime,
  formatTimeRange,
} from "@/lib/formatTime";
import { salonProfileQuery } from "../api/salon.queries";
import { FALLBACK_TIME_ZONE } from "./useSalonTimeZone";

export interface SalonClock {
  timeZone: string;
  clock: ClockFormat;
  /** "4:30 pm" or "16:30". */
  time: (instant: string) => string;
  /** "1:00 pm": a whole hour keeps its minutes, for times shown in a column. */
  slotTime: (instant: string) => string;
  /** "Fri, 16 Oct". */
  day: (instant: string) => string;
  /** "Fri, 16 Oct, 4:30 pm". */
  dateTime: (instant: string) => string;
  /** "4:30 – 5:20 pm". */
  range: (start: string, end: string) => string;
  parts: (instant: string) => ReturnType<typeof dateParts>;
}

/**
 * Every time on this app, in the salon's zone and written the way the salon chose
 * (`salon.public.time_format`, TDD-SA FR-SA-140). Until the profile loads, India Standard Time on the
 * 12-hour clock, the reference design's default.
 */
export function useSalonClock(): SalonClock {
  const { data } = useQuery(salonProfileQuery());
  const timeZone = data?.branch?.timezone ?? FALLBACK_TIME_ZONE;
  const clock: ClockFormat = data?.timeFormat ?? "12-hour";

  return useMemo(
    () => ({
      timeZone,
      clock,
      time: (instant) => formatTime(instant, timeZone, { clock }),
      slotTime: (instant) => formatTime(instant, timeZone, { clock, wholeHour: "full" }),
      day: (instant) => formatDay(instant, timeZone),
      dateTime: (instant) => formatDateTime(instant, timeZone, { clock }),
      range: (start, end) => formatTimeRange(start, end, timeZone, { clock }),
      parts: (instant) => dateParts(instant, timeZone),
    }),
    [timeZone, clock],
  );
}
